import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '叮当漫画网',
    url: 'http://kjbooks.cn/',
    description: '国内领先的免费漫画阅读平台，汇聚数千部优质漫画作品。',
    foundingDate: '2020-03',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutData.contact.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
    },
    sameAs: [
      'http://kjbooks.cn/',
    ],
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于叮当漫画网',
    url: 'http://kjbooks.cn/about',
    description: '了解叮当漫画网的品牌故事、平台数据和商务合作联系方式。',
    isPartOf: { '@id': 'http://kjbooks.cn/#website' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-3 py-1 border border-background-300/50 text-foreground-400 text-xs rounded-full mb-4 whitespace-nowrap">
                关于叮当
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-100 leading-relaxed">
                {aboutData.mission}
              </h1>
              <p className="mt-6 text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                成立于{aboutData.founded}，{aboutData.brandName}是国内领先的免费漫画阅读平台，采用创新的下拉式阅读技术，为漫画爱好者打造最舒适便捷的在线阅读体验。
              </p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                {aboutData.stats.map((stat, index) => (
                  <div key={index} className="bg-background-50 rounded-xl p-6 md:p-8 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500/10 flex items-center justify-center">
                      <i className={`${stat.icon} text-primary-500 text-2xl`}></i>
                    </div>
                    <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-100">{stat.value}</p>
                    <p className="text-foreground-500 text-sm mt-2">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6">品牌故事</h2>
                  <div className="space-y-4 text-foreground-500 text-sm md:text-base leading-relaxed">
                    {aboutData.description.split('\n\n').map((paragraph, i) => (
                      <p key={i}>{paragraph}</p>
                    ))}
                  </div>
                  <div className="mt-8">
                    <img
                      src="https://readdy.ai/api/search-image?query=Cozy%20reading%20corner%20with%20warm%20lighting%2C%20person%20reading%20manga%20on%20tablet%2C%20bookshelves%20in%20background%2C%20warm%20amber%20and%20brown%20tones%2C%20comfortable%20atmosphere%2C%20modern%20interior%20design%2C%20editorial%20photography%20style&width=800&height=450&seq=about-brand-reading&orientation=landscape"
                      alt="叮当漫画网 - 阅读体验"
                      title="叮当漫画网 - 优质阅读体验"
                      className="w-full rounded-2xl object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div id="contact">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-6">商务合作</h2>
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8 space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-300 text-sm font-medium">合作邮箱</p>
                        <p className="text-foreground-100 text-sm mt-1">{aboutData.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-wechat-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-300 text-sm font-medium">官方微信</p>
                        <p className="text-foreground-100 text-sm mt-1">{aboutData.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-links-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-300 text-sm font-medium">官方微博</p>
                        <p className="text-foreground-100 text-sm mt-1">{aboutData.contact.siteLink}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-300 text-sm font-medium">联系电话</p>
                        <p className="text-foreground-100 text-sm mt-1">{aboutData.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-foreground-300 text-sm font-medium">公司地址</p>
                        <p className="text-foreground-100 text-sm mt-1">{aboutData.contact.address}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 bg-background-100 rounded-2xl p-6 md:p-8">
                    <h3 className="text-foreground-200 font-heading text-lg font-bold mb-3">合作类型</h3>
                    <ul className="space-y-2 text-foreground-500 text-sm">
                      <li className="flex items-center gap-2">
                        <i className="ri-check-line text-accent-500"></i>
                        漫画内容授权合作
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-check-line text-accent-500"></i>
                        广告投放与品牌推广
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-check-line text-accent-500"></i>
                        版权代理与IP开发
                      </li>
                      <li className="flex items-center gap-2">
                        <i className="ri-check-line text-accent-500"></i>
                        跨界联名与活动策划
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}